import { ISecurityGroup, IUser, SecurityPermissionLevel } from '../api-client/autogenerated';

/**
 * Pure permission predicates for bidding-related write actions.
 *
 * These intentionally live in a dependency-light module (types + enum only) so
 * they can be unit tested without pulling in the axios client / redux store, and
 * so components can reuse one canonical write-gate instead of re-deriving it.
 *
 * Admins (site admins and subscriber admins of the project) always pass. For
 * everyone else, the relevant module permission must be ALL (NUMBER_4). A VIEW
 * (NUMBER_0) user never passes.
 */

type ProjectLike = { subscriberId?: string | null } | null | undefined;

const isAdminOfProject = (user: IUser, project: ProjectLike): boolean =>
  user.isSiteAdmin === true ||
  (!!project?.subscriberId && user.adminOfSubscriberId === project.subscriberId);

/** Whether the user may upload/replace a completed bid tabulation file. */
export const canUploadBidTabulation = (
  user?: IUser | null,
  security?: ISecurityGroup | null,
  project?: ProjectLike,
): boolean => {
  if (!user) return false;
  if (isAdminOfProject(user, project)) return true;
  return security?.bidTabulationPermission === SecurityPermissionLevel.NUMBER_4;
};

/** Whether the user may edit bid management setup (incl. deleting spec/drawing files). */
export const canEditBidManagement = (
  user?: IUser | null,
  security?: ISecurityGroup | null,
  project?: ProjectLike,
): boolean => {
  if (!user) return false;
  if (isAdminOfProject(user, project)) return true;
  return security?.bidManagementPermission === SecurityPermissionLevel.NUMBER_4;
};

/**
 * Whether the "restrict submission to prime bidders only" bid setting blocks this
 * user from submitting a Bidder RFI or Substitution Request in the bidding portal.
 */
export const requiresPrimeBidderToSubmit = (
  bidSetup?: { restrictSubmissionToPrimeBidders?: boolean | null } | null,
  isPrimeBidder?: boolean,
): boolean => !!bidSetup?.restrictSubmissionToPrimeBidders && !isPrimeBidder;
